import React, { useState } from 'react';
import { 
  CloudArrowUpIcon, 
  ArrowRightIcon, 
  TrashIcon, 
  TableCellsIcon, 
  MapPinIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  EllipsisHorizontalIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { RocketLaunchIcon } from '@heroicons/react/24/solid';

export default function BulkUpload() {
  const [selectedMode, setSelectedMode] = useState('deep-x2');

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 px-10 py-12 overflow-y-auto">
        <div className="max-w-[1400px] mx-auto">
          
          {/* Header & Stepper */}
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-8 mb-12">
            <div>
              <h1 className="text-[36px] font-extrabold text-slate-900 tracking-tight">Bulk Upload</h1>
              <p className="text-[16px] text-slate-500 mt-2 font-medium">
                Batch process records with intelligent AI extraction.
              </p>
            </div>
            
            <div className="flex items-center gap-5 text-[15px] font-bold">
              {/* Step 1 */}
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-brand-500 text-white flex items-center justify-center text-[13px] shadow-sm">1</span>
                <span className="text-slate-900">Upload</span>
              </div>
              <div className="w-12 h-px bg-slate-300"></div>
              {/* Step 2 */}
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-white border border-slate-200 text-slate-400 flex items-center justify-center text-[13px]">2</span>
                <span className="text-slate-400">Mapping</span>
              </div>
              <div className="w-12 h-px bg-slate-300"></div>
              {/* Step 3 */}
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-white border border-slate-200 text-slate-400 flex items-center justify-center text-[13px]">3</span>
                <span className="text-slate-400">Process</span>
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">
            
            {/* Left Column (Upload & Mapping) */}
            <div className="xl:col-span-8 flex flex-col gap-8">
              
              {/* Upload Area */}
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-[32px] p-16 flex flex-col items-center justify-center text-center relative overflow-hidden group">
                <div className="w-24 h-24 bg-brand-50 rounded-full flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <CloudArrowUpIcon className="w-12 h-12 text-brand-500" />
                </div>
                <h3 className="text-[28px] font-extrabold text-slate-900 mb-4 tracking-tight">Import your data</h3>
                <p className="text-[16px] text-slate-500 mb-10 max-w-lg leading-relaxed">
                  Drag and drop your CSV file here. <span className="text-red-500 font-semibold">Company Name and Company Location</span> are required fields.
                </p>
                <div className="flex items-center gap-4 mb-12">
                  <button className="bg-[#0f172a] text-white px-8 py-3.5 rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/10">
                    Browse Files
                  </button>
                  <button className="bg-white border border-slate-200 text-slate-700 px-8 py-3.5 rounded-xl font-bold hover:bg-slate-50 transition-colors shadow-sm">
                    Download Template
                  </button>
                </div>
                <div className="flex items-center gap-6 text-[11px] font-bold text-slate-400 uppercase tracking-[0.15em]">
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-md">
                    <TableCellsIcon className="w-4 h-4" /> MAX 1M ROWS
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-md">
                    <RocketLaunchIcon className="w-4 h-4" /> DEEP X1
                  </div>
                </div>
              </div>

              {/* Intelligent Field Mapping */}
              <div className="bg-white border border-slate-200 rounded-[32px] overflow-hidden shadow-sm">
                <div className="px-10 py-6 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-brand-500"></div>
                    <h3 className="text-[13px] font-bold text-slate-600 uppercase tracking-widest">Intelligent Field Mapping</h3>
                  </div>
                  <span className="px-4 py-1.5 bg-brand-50 text-brand-600 text-xs font-bold rounded-full border border-brand-100">
                    AI CONFIDENCE: 94%
                  </span>
                </div>
                
                <div className="p-10 flex flex-col gap-6">
                  {/* Mapping Row 1 */}
                  <div className="flex items-center justify-between gap-8 p-6 rounded-[20px] border border-slate-100 bg-white shadow-sm hover:border-slate-200 hover:shadow-md transition-all">
                    <div className="flex-1">
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 pl-1">CSV Column</p>
                      <div className="flex items-center gap-3 text-slate-700 font-bold bg-slate-50/80 px-5 py-3 rounded-xl border border-slate-100">
                        <TableCellsIcon className="w-5 h-5 text-slate-400" />
                        company_legal_name
                      </div>
                    </div>
                    <div className="pt-7">
                      <ArrowRightIcon className="w-5 h-5 text-slate-300" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 pl-1">Field Name</p>
                      <div className="flex items-center justify-between text-slate-900 font-bold bg-white px-5 py-3 rounded-xl border border-slate-200 shadow-sm cursor-pointer hover:border-slate-300 transition-colors">
                        Company Name
                        <ChevronDownIcon className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                    <div className="pt-7">
                      <button className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Mapping Row 2 */}
                  <div className="flex items-center justify-between gap-8 p-6 rounded-[20px] border border-slate-100 bg-white shadow-sm hover:border-slate-200 hover:shadow-md transition-all">
                    <div className="flex-1">
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 pl-1">CSV Column</p>
                      <div className="flex items-center gap-3 text-slate-700 font-bold bg-slate-50/80 px-5 py-3 rounded-xl border border-slate-100">
                        <MapPinIcon className="w-5 h-5 text-slate-400" />
                        Location of Company
                      </div>
                    </div>
                    <div className="pt-7">
                      <ArrowRightIcon className="w-5 h-5 text-slate-300" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 pl-1">Field Name</p>
                      <div className="flex items-center justify-between text-slate-900 font-bold bg-white px-5 py-3 rounded-xl border border-slate-200 shadow-sm cursor-pointer hover:border-slate-300 transition-colors">
                        Company Location
                      </div>
                    </div>
                    <div className="pt-7">
                      <button className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="px-10 py-6 border-t border-slate-100 bg-slate-50/50">
                  <p className="text-[12px] font-bold text-slate-400 uppercase tracking-widest">
                    12 OF 14 COLUMNS MAPPED
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column (Controls & Log) */}
            <div className="xl:col-span-4 flex flex-col gap-8">
              
              {/* Processing Mode */}
              <div className="bg-white border border-slate-200 rounded-[32px] p-8 shadow-sm">
                <h2 className="text-[22px] font-extrabold text-slate-900 mb-6 tracking-tight">Processing Mode</h2>
                
                <div className="flex flex-col gap-4 mb-8">
                  {/* Mode 1: Normal Search */}
                  <label className={`flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-all ${selectedMode === 'normal' ? 'border-brand-500 bg-brand-50/30' : 'border-slate-100 hover:border-slate-200'}`} onClick={() => setSelectedMode('normal')}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <DocumentTextIcon className={`w-5 h-5 ${selectedMode === 'normal' ? 'text-brand-500' : 'text-slate-400'}`} />
                        <h4 className="font-bold text-slate-900 text-[15px]">Normal Search</h4>
                      </div>
                      <p className="text-[13px] text-slate-500 leading-relaxed pl-7">Basic search. Fastest, good results.</p>
                    </div>
                    <div className="pt-1 pr-1">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${selectedMode === 'normal' ? 'border-brand-500' : 'border-slate-300'}`}>
                        {selectedMode === 'normal' && <div className="w-2.5 h-2.5 rounded-full bg-brand-500"></div>}
                      </div>
                    </div>
                  </label>

                  {/* Mode 2: Deep Search */}
                  <label className={`flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-all ${selectedMode === 'deep' ? 'border-brand-500 bg-brand-50/30' : 'border-slate-100 hover:border-slate-200'}`} onClick={() => setSelectedMode('deep')}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <MagnifyingGlassIcon className={`w-5 h-5 ${selectedMode === 'deep' ? 'text-brand-500' : 'text-slate-400'}`} />
                        <h4 className="font-bold text-slate-900 text-[15px]">Deep Search</h4>
                      </div>
                      <p className="text-[13px] text-slate-500 leading-relaxed pl-7">In depth queries. Becomes slower the deeper you search. Excellent results.</p>
                    </div>
                    <div className="pt-1 pr-1">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${selectedMode === 'deep' ? 'border-brand-500' : 'border-slate-300'}`}>
                        {selectedMode === 'deep' && <div className="w-2.5 h-2.5 rounded-full bg-brand-500"></div>}
                      </div>
                    </div>
                  </label>

                  {/* Mode 3: Deep Search X2 */}
                  <label className={`flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-all ${selectedMode === 'deep-x2' ? 'border-brand-500 bg-brand-50/50' : 'border-slate-100 hover:border-slate-200'}`} onClick={() => setSelectedMode('deep-x2')}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <RocketLaunchIcon className={`w-5 h-5 ${selectedMode === 'deep-x2' ? 'text-brand-500' : 'text-slate-400'}`} />
                        <h4 className="font-bold text-slate-900 text-[15px]">Deep Search X2</h4>
                      </div>
                      <p className="text-[13px] text-slate-500 leading-relaxed pl-7">1 Level deeper than the default deep search.</p>
                    </div>
                    <div className="pt-1 pr-1">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${selectedMode === 'deep-x2' ? 'border-brand-500' : 'border-slate-300'}`}>
                        {selectedMode === 'deep-x2' && <div className="w-2.5 h-2.5 rounded-full bg-brand-500"></div>}
                      </div>
                    </div>
                  </label>
                </div>

                <div className="flex items-center justify-between mb-5 px-2">
                  <span className="text-[12px] font-bold text-slate-400 uppercase tracking-widest">Est. Credits</span>
                  <span className="text-xl font-extrabold text-slate-900">1,420</span>
                </div>

                <button className="w-full bg-brand-500 text-white py-4 rounded-[14px] font-bold flex items-center justify-center gap-2 hover:bg-brand-600 transition-all shadow-lg shadow-brand-500/25 text-[15px]">
                  <RocketLaunchIcon className="w-5 h-5" /> Initiate Batch
                </button>
              </div>

              {/* Activity Log */}
              <div>
                <div className="flex items-center justify-between mb-5 px-1">
                  <h3 className="text-[12px] font-bold text-slate-500 uppercase tracking-widest">Activity Log</h3>
                  <button className="text-[13px] font-bold text-brand-500 hover:text-brand-700 transition-colors">
                    Full History
                  </button>
                </div>

                <div className="flex flex-col gap-4">
                  {/* Log Item 1: Success */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex gap-3.5">
                        <div className="mt-0.5">
                          <CheckCircleIcon className="w-6 h-6 text-emerald-500" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 text-[14px]">q4_market_signals</h4>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Completed • 4m ago</p>
                        </div>
                      </div>
                      <button className="text-slate-300 hover:text-slate-500"><EllipsisHorizontalIcon className="w-6 h-6" /></button>
                    </div>
                    <div className="flex gap-4 ml-9">
                      <button className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600 hover:text-slate-900 uppercase tracking-wider">
                        <ArrowDownTrayIcon className="w-4 h-4" /> Dataset
                      </button>
                      <button className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600 hover:text-slate-900 uppercase tracking-wider">
                        <DocumentTextIcon className="w-4 h-4" /> Report
                      </button>
                    </div>
                  </div>

                  {/* Log Item 2: Processing */}
                  <div className="bg-[#0f172a] rounded-2xl p-5 shadow-lg shadow-slate-900/10">
                    <div className="flex items-start justify-between mb-5">
                      <div>
                        <h4 className="font-bold text-white text-[14px]">investor_leads_v2.csv</h4>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Processing 2,400 Records</p>
                      </div>
                      <div className="flex gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse"></div>
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse delay-75"></div>
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse delay-150"></div>
                      </div>
                    </div>
                    <div className="w-full h-1.5 bg-slate-800 rounded-full mb-3 overflow-hidden">
                      <div className="h-full bg-brand-500 rounded-full w-[72%]"></div>
                    </div>
                    <div className="flex items-center justify-between text-[11px] font-bold">
                      <span className="text-slate-300">72% Complete</span>
                      <span className="text-white">ETA : 12 Min</span>
                    </div>
                  </div>

                  {/* Log Item 3: Error */}
                  <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
                    <div className="flex items-start gap-3.5 mb-4">
                      <div className="mt-0.5">
                        <ExclamationCircleIcon className="w-6 h-6 text-red-500" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 text-[14px]">legacy_crm_export.xlsx</h4>
                        <p className="text-[10px] font-bold text-red-500 uppercase tracking-wider mt-1">Header format mismatch</p>
                      </div>
                    </div>
                    <div className="ml-9">
                      <button className="bg-white border border-slate-200 text-slate-700 px-4 py-1.5 rounded-lg text-[12px] font-bold hover:bg-slate-50 transition-colors shadow-sm">
                        Fix Errors
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-5 px-10 shrink-0">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
              System Status: <span className="text-slate-900">Operational</span>
            </span>
          </div>
          <div className="flex items-center gap-8 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
            <a href="#" className="hover:text-slate-600 transition-colors">Privacy</a>
            <a href="#" className="hover:text-slate-600 transition-colors">Terms</a>
            <a href="#" className="hover:text-slate-600 transition-colors">API Docs</a>
            <span>© 2024 FounderFinder</span>
          </div>
        </div>
      </footer>
    </div>
  );
}